import { useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { AppFooter } from "@/app/layout/app-footer";
import { PublicHeader } from "@/app/layout/public-header";
import { DeniedPersonRefusalError, type DeniedPersonRefusalReason } from "@/core/auth/auth-client";
import { useAuth } from "@/core/auth/auth-provider";
import {
  consumePersistedReturnTo,
  getDefaultReturnTo,
  normalizeReturnTo,
  persistReturnTo,
} from "@/core/auth/return-to";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { PublicBrandHeader } from "@/shared/components/public-brand-header";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui";

// Friendly messages for auth-service redirect error codes. The `error` query param
// is untrusted user-controlled input: it is only used as a lookup key and is never
// rendered verbatim; anything unrecognized gets the fallback.
const LOGIN_ERROR_MESSAGES: Record<string, { title: string; description: ReactNode }> = {
  invalid_state: {
    title: "Sign-in session expired",
    description: "Your sign-in attempt expired or was interrupted. Please try again.",
  },
  admin_access_denied: {
    title: "Access denied",
    description: "Your account is not authorized for CLM Web administration.",
  },
  admin_access_unknown_person: {
    title: "Access denied",
    description: "Your account could not be matched to a CLM person record.",
  },
  admin_access_deactivated_role: {
    title: "Access denied",
    description: "Your CLM administrator role is deactivated.",
  },
  admin_access_quarantined: {
    title: "Access denied",
    description: "Your account is temporarily quarantined. Contact an administrator.",
  },
  admin_access_stale_claim: {
    title: "Sign-in could not be completed",
    description: "Your sign-in information is out of date. Please try signing in again.",
  },
  // FR-019: a deleted legacy member gets the generic failure (never says why);
  // legacy text "Login failed. Invalid user name or password."
  legacy_deleted: {
    title: "Sign-in failed",
    description: "Login failed. Invalid user name or password.",
  },
  // FR-019: legacy membership-expired text; the address is rendered as a mailto link.
  membership_expired: {
    title: "Sign-in failed",
    description: (
      <>
        Oh no - your membership has expired! To regain access to your member benefits, please email{" "}
        <a
          href="mailto:membership@theclm.org"
          className="rounded-sm font-semibold underline underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          membership@theclm.org
        </a>
        . Thank you!
      </>
    ),
  },
};

const LOGIN_ERROR_FALLBACK = {
  title: "Sign-in could not be completed",
  description: "Something interrupted your sign-in. Please try again.",
};

// Entra single logout redirects back to /login with SAML artifacts appended.
// They are harmless but leave a signed SAML payload in the address bar and
// browser history, so they are stripped on arrival.
const SAML_LOGOUT_PARAMS = ["SAMLResponse", "RelayState", "SigAlg", "Signature"];

export function LoginRoute(): ReactElement {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = normalizeReturnTo(searchParams.get("returnTo"), getDefaultReturnTo());
  const errorCode = searchParams.get("error");
  const loginError = errorCode ? (LOGIN_ERROR_MESSAGES[errorCode] ?? LOGIN_ERROR_FALLBACK) : null;
  const loginUrl = auth.client.getLoginUrl(returnTo);

  useEffect(() => {
    persistReturnTo(returnTo);
  }, [returnTo]);

  useEffect(() => {
    if (!SAML_LOGOUT_PARAMS.some((param) => searchParams.has(param))) {
      return;
    }

    const cleanedParams = new URLSearchParams(searchParams);
    for (const param of SAML_LOGOUT_PARAMS) {
      cleanedParams.delete(param);
    }
    setSearchParams(cleanedParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <PublicAuthCard
      title="Sign in to CLM Web"
      description="Continue with the CLM auth service to start a secure session."
    >
      {loginError ? (
        <Alert variant="destructive">
          <AlertTitle>{loginError.title}</AlertTitle>
          <AlertDescription>{loginError.description}</AlertDescription>
        </Alert>
      ) : null}
      <Button asChild className="w-full">
        <a href={loginUrl}>Continue to sign in</a>
      </Button>
    </PublicAuthCard>
  );
}

export function AuthCallbackRoute(): ReactElement {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasHandledCallback = useRef(false);
  // refreshSession() updates auth context state, which re-runs a deps-based
  // effect mid-flight; refs keep the one-shot completion from being cancelled.
  const authRef = useRef(auth);
  authRef.current = auth;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // The completion outlives the route when a refusal redirect replaces it; it then navigates nowhere.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasHandledCallback.current) {
      return;
    }

    hasHandledCallback.current = true;
    const requestedReturnTo = normalizeReturnTo(
      searchParams.get("returnTo"),
      consumePersistedReturnTo(getDefaultReturnTo()),
    );

    async function completeCallback(): Promise<void> {
      try {
        await authRef.current.refreshSession();
        if (mountedRef.current) {
          void navigateRef.current(requestedReturnTo, { replace: true });
        }
      } catch (error) {
        if (!mountedRef.current) {
          return;
        }
        // The auth service refused a denied Person: show its login alert, not the error page.
        const target =
          error instanceof DeniedPersonRefusalError
            ? deniedPersonRefusalLoginPath(error.code)
            : "/error";
        void navigateRef.current(target, { replace: true });
      }
    }

    void completeCallback();
  }, [searchParams]);

  return (
    <PublicAuthCard
      title="Completing sign in"
      description="Checking your CLM auth-service session. No secrets are processed in the frontend callback."
    >
      <AuthSpinner label="Completing sign in" />
    </PublicAuthCard>
  );
}

/**
 * Brings the user to the sign-in result when an access-token request is refused for a denied
 * Person (FR-019), wherever they are. The auth provider sits above the router, so the router
 * reacts to its refusal state. Fires once per refusal: the navigate function changes with
 * every location, so it is read through a ref.
 */
export function DeniedPersonRefusalRedirect(): null {
  const { deniedPersonRefusal } = useAuth();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (deniedPersonRefusal) {
      void navigateRef.current(deniedPersonRefusalLoginPath(deniedPersonRefusal), {
        replace: true,
      });
    }
  }, [deniedPersonRefusal]);

  return null;
}

// The `error` value is the enum member, never text taken from the response.
function deniedPersonRefusalLoginPath(code: DeniedPersonRefusalReason): string {
  return `/login?error=${code}`;
}

export function LogoutRoute(): ReactElement {
  const auth = useAuth();

  useEffect(() => {
    void auth.logout();
  }, [auth]);

  return (
    <PublicAuthCard
      title="Signing out"
      description="Clearing the CLM Web memory-only token and ending the auth-service session."
    >
      <AuthSpinner label="Signing out" />
    </PublicAuthCard>
  );
}

export function ErrorRoute(): ReactElement {
  const location = useLocation();

  return (
    <PublicAuthCard
      title="CLM Web error"
      description="Something prevented CLM Web from completing the requested action."
    >
      <p className="text-sm text-muted-foreground">Path: {location.pathname}</p>
      <Button asChild variant="outline">
        <Link to="/dashboard">Return to dashboard</Link>
      </Button>
    </PublicAuthCard>
  );
}

export function HealthRoute(): ReactElement {
  return (
    <PublicAuthCard
      title="CLM Web health"
      description="The SPA shell is reachable. This page intentionally exposes no runtime secrets."
    />
  );
}

function PublicAuthCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}): ReactElement {
  // FR-040: public auth surfaces carry the full global chrome (branded
  // header with a sign-in entry point, shared footer) with no sidebar nav.
  // 011-24: solid clm-primary-blue backdrop behind a centered white card with
  // the CLM wordmark (wordmark only per the 2026-08-24 designer ruling).
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <PublicHeader />
      <main className="clm-page-gutter flex flex-1 items-center justify-center bg-clm-primary-blue py-12">
        <Card className="w-full max-w-lg border-0 bg-white px-2 py-8 shadow-md sm:px-8">
          <CardHeader className="text-center">
            <PublicBrandHeader className="mb-6" />
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          {children ? <CardContent className="flex flex-col gap-4">{children}</CardContent> : null}
        </Card>
      </main>
      <AppFooter />
    </div>
  );
}

// Loading state via the shared ICON_MAP "loading" entry (Carbon CircleDash) spun blue with a short text label.
function AuthSpinner({ label }: { label: string }): ReactElement {
  return (
    <div
      className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground"
      role="status"
    >
      <ClmIcon name="loading" decorative className="h-6 w-6 animate-spin text-clm-brand-blue" />
      <span>{label}</span>
    </div>
  );
}
